<!-- Search + Clear -->
<div class="theme-search">
  <div class="theme-search__field">
    <span class="theme-search__icon" aria-hidden="true">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="7"/>
        <path d="m20 20-3-3"/>
      </svg>
    </span>
    <input
      type="text"
      class="theme-search__input"
      placeholder="Search themes"
      [value]="searchQuery"
      (input)="onSearchInput($any($event.target).value)"
      aria-label="Search themes"
    />
  </div>
  <button
    type="button"
    class="theme-search__clear"
    [class.is-disabled]="!searchQuery && selectedCategory === 'All'"
    (click)="clearSearch()"
    ssTooltip="Clear search and category">
    Clear
  </button>
</div>

<!-- Category pills -->
<div class="theme-pills" role="tablist" aria-label="Theme categories">
  @for (name of categoryNames; track name) {
    <button
      type="button"
      class="theme-pill"
      role="tab"
      [class.is-active]="selectedCategory === name"
      [attr.aria-selected]="selectedCategory === name"
      (click)="selectCategory(name)"
    >{{ name }}</button>
  }
</div>

<!-- Flat theme grid -->
<div class="theme-grid theme-grid--flat">
  @for (t of filteredThemes; track t.id) {
    <button
      type="button"
      class="theme-card"
      [class.theme-card--active]="t.id === activeId"
      [attr.aria-pressed]="t.id === activeId"
      [ssTooltip]="t.name"
      [style.--card-theme-bg]="cardBg(t)"
      (click)="select(t)"
      (dblclick)="selectAndCommit(t)"
    >
      @if (isAnimated(t)) {
        <span class="theme-live-dot" aria-hidden="true"></span>
      }
      <span class="theme-colorbg-dot" aria-hidden="true"></span>
      <div class="theme-swatches">
        <span class="theme-swatch" [style.background]="previewColors(t).mint"></span>
        <span class="theme-swatch" [style.background]="previewColors(t).pink"></span>
        <span class="theme-swatch" [style.background]="previewColors(t).green"></span>
      </div>
      <span class="theme-label" [style.color]="t.id !== activeId ? cardLabelColor(t) : null">{{ t.name }}</span>
      @if (!t.builtIn) {
        <button
          type="button"
          class="theme-download"
          aria-label="Export theme"
          ssTooltip="Export as .singsnap-theme.json"
          (click)="downloadTheme($event, t)"
        >
          <svg width="8" height="8" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M6 1v7M3 6l3 3 3-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M1 11h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
        <button
          type="button"
          class="theme-delete"
          aria-label="Remove theme"
          ssTooltip="Remove this theme"
          (click)="deleteCustom($event, t)"
        >×</button>
      }
    </button>
  }
  @if (filteredThemes.length === 0) {
    <p class="theme-empty">No themes match.</p>
  }
</div>

<button
  type="button"
  class="theme-advanced-toggle"
  [class.is-open]="advancedOpen"
  [attr.aria-expanded]="advancedOpen"
  ssTooltip="Show theme tools — export, load, edit, create"
  (click)="toggleAdvanced()"
>
  <span>Advanced</span>
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
</button>

@if (advancedOpen) {
  <div class="theme-actions theme-actions--file">
    <button type="button" class="theme-action-btn"
            ssTooltip="Export active theme as .singsnap-theme.json"
            (click)="downloadTheme($event, themeService.activeTheme)">
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M6 1v7M3 6l3 3 3-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M1 10h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
      Export
    </button>

    <label class="theme-action-btn" ssTooltip="Load a .singsnap-theme.json file">
      <input type="file" accept=".json" (change)="onFileSelected($event)" style="display:none">
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M6 9V2M3 5l3-3 3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M1 10h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
      Load
    </label>
  </div>

  @if (loadError) {
    <p class="theme-load-error">{{ loadError }}</p>
  }

  <div class="theme-actions">
    <button type="button" class="theme-action-btn" [class.is-active]="builderOpen && editingThemeId"
            ssTooltip="Edit the active theme's colors"
            (click)="editTheme()">
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M8.5 1.5l2 2L3 11H1V9L8.5 1.5z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      Edit
    </button>

    <button type="button" class="theme-action-btn" [class.is-active]="builderOpen && !editingThemeId"
            ssTooltip="Create a custom theme from background, accent and highlight colors"
            (click)="toggleBuilder()">
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <line x1="6" y1="1" x2="6" y2="11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        <line x1="1" y1="6" x2="11" y2="6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
      Create
    </button>
  </div>

  @if (builderOpen) {
    <div class="theme-builder">
      @if (builderContextLabel) {
        <span class="theme-builder__context">Editing: {{ builderContextLabel }}</span>
      }
      <input
        class="theme-builder__name"
        type="text"
        placeholder="Theme name…"
        [value]="builderName"
        (input)="builderName = $any($event.target).value"
        maxlength="32"
      />
      <div class="theme-builder__colors">
        <label class="theme-builder__color-field" ssTooltip="Base background color — darkened automatically for the gradient">
          <span class="theme-builder__color-label">BG</span>
          <input type="color" [value]="builderBg" (focus)="onColorInputFocus()" (input)="onBuilderColorChange('bg', $any($event.target).value)" />
          <span class="theme-builder__color-swatch" [style.background]="builderBg"></span>
        </label>
        <label class="theme-builder__color-field" ssTooltip="Primary accent — controls, sliders, active states">
          <span class="theme-builder__color-label">Accent</span>
          <input type="color" [value]="builderAccent" (focus)="onColorInputFocus()" (input)="onBuilderColorChange('accent', $any($event.target).value)" />
          <span class="theme-builder__color-swatch" [style.background]="builderAccent"></span>
        </label>
        <label class="theme-builder__color-field" ssTooltip="Highlight color — record button, secondary accents">
          <span class="theme-builder__color-label">Highlight</span>
          <input type="color" [value]="builderHighlight" (focus)="onColorInputFocus()" (input)="onBuilderColorChange('highlight', $any($event.target).value)" />
          <span class="theme-builder__color-swatch" [style.background]="builderHighlight"></span>
        </label>
      </div>

      <label class="theme-builder__toggle" ssTooltip="Set an explicit bright endpoint for the Brightness slider (Romantic-style dark→bright shift)">
        <input type="checkbox" [checked]="builderUseFillLight" (change)="toggleBuilderFillLight()" />
        <span>Custom bright endpoint</span>
      </label>

      @if (builderUseFillLight) {
        <div class="theme-builder__colors">
          <label class="theme-builder__color-field" ssTooltip="Bright endpoint — the color the background shifts toward at Brightness 100">
            <span class="theme-builder__color-label">Bright</span>
            <input type="color" [value]="builderBright" (focus)="onColorInputFocus()" (input)="onBuilderColorChange('bright', $any($event.target).value)" />
            <span class="theme-builder__color-swatch" [style.background]="builderBright"></span>
          </label>
        </div>
      }

      <div class="theme-builder__actions">
        <button
          type="button"
          class="theme-builder__cancel"
          (click)="cancelBuilder()"
        >Cancel</button>
        <button
          type="button"
          class="theme-builder__save"
          [disabled]="!builderName.trim()"
          (click)="saveTheme()"
        >{{ builderSaveLabel }}</button>
      </div>
    </div>
  }
}
